import { useContext, useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { getCommon } from '../../API/query/common';
import { handleMapToOptions } from '../functions/handleMapToOptions';
import { sortAlphabetically } from '../functions/sortAlphabetically';
import { AuthContext } from '@/common/contexts/authContext';

const COMMON_CACHE_TIME = 1000 * 60 * 30;

const EMPTY_COMMON_DATA = {
	users: [],
	markets: [],
	products: [],
	queues: [],
	usersOptions: [],
	productsOptions: [],
	marketsOptions: [],
	queuesOptions: [],
	COUNTRIES: [],
};

const handleSetOptions = (successData) => {
	successData.usersOptions = handleMapToOptions(
		successData.users,
		'username',
		'_id',
	);
	successData.marketsOptions = sortAlphabetically(
		handleMapToOptions(successData.markets, 'name', '_id'),
		'label',
	);
	successData.productsOptions = sortAlphabetically(
		handleMapToOptions(successData.products, 'name', '_id'),
		'label',
	);
	successData.queuesOptions = handleMapToOptions(
		successData.queues?.sort((qA, qB) => qB.select_weight - qA.select_weight),
		'name',
	);
	successData.COUNTRIES = handleMapToOptions(
		successData.markets,
		'name',
		'short',
	);
};

export const useCommonData = () => {
	const { authUser } = useContext(AuthContext);

	const { data, refetch, isLoading, isFetching } = useQuery(
		['common', authUser?.id],
		async () => {
			const raw = await getCommon();
			handleSetOptions(raw);
			return raw;
		},
		{
			enabled: !!authUser,
			placeholderData: EMPTY_COMMON_DATA,
			refetchInterval: COMMON_CACHE_TIME,
			refetchOnWindowFocus: false,
		},
	);

	const {
		users,
		products,
		markets,
		queues,
		usersOptions,
		productsOptions,
		marketsOptions,
		queuesOptions,
		COUNTRIES,
	} = data;

	const options = {
		usersOptions,
		marketsOptions,
		productsOptions,
		queuesOptions,
	};
	const commonData = { users, markets, queues, products };
	const queryFunctions = { refetch };
	const queryValues = { isFetching, isLoading };

	const filterUsersByRoles = useCallback(
		(roles, onlyActive = false) => {
			return handleMapToOptions(
				users.filter((user) =>
					onlyActive
						? roles.includes(user.role) && user.active
						: roles.includes(user.role),
				),
				'username',
				'_id',
			);
		},
		[users],
	);

	const getActiveUsersOptions = useCallback(() => {
		return handleMapToOptions(
			users.filter((user) => user.active),
			'username',
			'_id',
		);
	}, [users]);

	return {
		commonData,
		options,
		filterUsersByRoles,
		getActiveUsersOptions,
		queryValues,
		queryFunctions,
		COUNTRIES,
	};
};
